import { Controller } from "@hotwired/stimulus"
import { MAP_STYLE, MAP_CONFIG } from "../custom/map_style"

// Connects to data-controller="site-directions"
//
// The site directions modal (TASK-247.2, decision-68 §4). Everything about
// the ROUTE happens in the browser: an address picked from the existing
// autocomplete, or the browser's own geolocation, geocodes to a lat/lng we
// already have — no server round trip — and from there this calls Mapbox
// Directions v5 on the SAME public token the map pickers use. No new
// server-side API key.
//
// Mounted on the modal's form-container either way (see the view) because
// "Copy address" needs it even when there's no map to draw — connect()
// skips the Mapbox half itself when the view rendered no map target
// (no token, or the site has no coordinates — Sites::DirectionsController
// decided that server-side).
export default class extends Controller {
  static targets = [
    "map", "originInput", "originLatitude", "originLongitude",
    "routeSummary", "routeDuration", "leaveBy",
    "apiError", "apiErrorText",
    "appleLink", "googleLink"
  ]

  static values = {
    token: String,
    destLat: Number,
    destLng: Number,
    destLabel: String,
    sessionToken: String,
    visitStartTime: String
  }

  async connect() {
    if (!this.tokenValue || !this.hasMapTarget) return

    const mapboxgl = await this.loadMapboxGL()
    if (!mapboxgl) return

    this.mapboxgl = mapboxgl
    mapboxgl.accessToken = this.tokenValue

    this.map = new mapboxgl.Map({
      container: this.mapTarget,
      style: MAP_STYLE,
      config: MAP_CONFIG,
      center: [this.destLngValue, this.destLatValue],
      zoom: 13
    })
    this.map.addControl(new mapboxgl.NavigationControl(), "top-right")

    // Orange-600: the one map-marker colour app-wide (DESIGN.md, TASK-202).
    this.destinationMarker = new mapboxgl.Marker({ color: "#E05E0B" })
      .setLngLat([this.destLngValue, this.destLatValue])
      .setPopup(new mapboxgl.Popup({ className: "map-popup-flat" }).setText(this.destLabelValue))
      .addTo(this.map)
  }

  disconnect() {
    this.map?.remove()
    this.map = null
  }

  // Fired by the generic `autocomplete` controller wrapping the Starting
  // From input. We need coordinates, not a form's worth of address lines,
  // so this fetches the address itself rather than delegating to the
  // `address-autocomplete` controller those other callers use.
  async onAddressSelect(event) {
    const { id } = event.detail
    if (!id) return

    try {
      const sessionToken = this.sessionTokenValue || crypto.randomUUID()
      const response = await fetch(`/addresses/search/${encodeURIComponent(id)}?session_token=${sessionToken}`, {
        headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" }
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const address = await response.json()
      const lat = parseFloat(address.latitude)
      const lng = parseFloat(address.longitude)
      if (Number.isNaN(lat) || Number.isNaN(lng)) throw new Error("no coordinates")

      this.setOrigin(lat, lng, "Starting point")
    } catch (error) {
      console.error("site-directions: address lookup failed", error)
      this.showError("that address couldn't be located")
    }
  }

  useMyLocation() {
    if (!navigator.geolocation) {
      this.showError("this browser can't share your location")
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (this.hasOriginInputTarget) this.originInputTarget.value = "My location"
        this.setOrigin(position.coords.latitude, position.coords.longitude, "My location")
      },
      () => this.showError("location access was denied"),
      { enableHighAccuracy: false, timeout: 10000 }
    )
  }

  setOrigin(lat, lng, label) {
    if (this.hasOriginLatitudeTarget) this.originLatitudeTarget.value = lat
    if (this.hasOriginLongitudeTarget) this.originLongitudeTarget.value = lng
    this.updateMapLinks(lat, lng)

    if (this.originMarker) {
      this.originMarker.setLngLat([lng, lat])
    } else if (this.map) {
      // Blue-600: the starting point, matching the summary strip and the
      // route line itself.
      this.originMarker = new this.mapboxgl.Marker({ color: "#1572b4" })
        .setLngLat([lng, lat])
        .setPopup(new this.mapboxgl.Popup({ className: "map-popup-flat" }).setText(label))
        .addTo(this.map)
    }

    this.fetchRoute(lat, lng)
  }

  async fetchRoute(lat, lng) {
    this.hideError()

    try {
      const url = "https://api.mapbox.com/directions/v5/mapbox/driving/" +
        `${lng},${lat};${this.destLngValue},${this.destLatValue}` +
        `?geometries=geojson&overview=full&access_token=${this.tokenValue}`
      const response = await fetch(url)
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const data = await response.json()
      const route = data.routes && data.routes[0]
      if (!route) throw new Error("no route in response")

      this.drawRoute(route.geometry)
      this.showSummary(route.duration, route.distance)
      this.fitToRoute(lat, lng)
    } catch (error) {
      console.error("site-directions: route fetch failed", error)
      this.showError("the route couldn't be loaded")
    }
  }

  drawRoute(geometry) {
    if (!this.map) return

    const sourceId = "site-directions-route"
    const data = { type: "Feature", properties: {}, geometry }

    if (this.map.getSource(sourceId)) {
      this.map.getSource(sourceId).setData(data)
      return
    }

    this.map.addSource(sourceId, { type: "geojson", data })
    // Blue-600, 6px — the one route-line colour (decision-68 §4).
    this.map.addLayer({
      id: sourceId,
      type: "line",
      source: sourceId,
      paint: { "line-color": "#1572b4", "line-width": 6 }
    })
  }

  fitToRoute(lat, lng) {
    if (!this.map) return

    const bounds = new this.mapboxgl.LngLatBounds()
    bounds.extend([lng, lat])
    bounds.extend([this.destLngValue, this.destLatValue])
    this.map.fitBounds(bounds, { padding: 60, maxZoom: 15 })
  }

  showSummary(durationSeconds, distanceMetres) {
    if (!this.hasRouteSummaryTarget) return

    const miles = Math.round(distanceMetres / 1609.34)
    const hours = Math.floor(durationSeconds / 3600)
    const minutes = Math.round((durationSeconds % 3600) / 60)
    const time = hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`

    if (this.hasRouteDurationTarget) this.routeDurationTarget.textContent = `${time} · ${miles} miles`
    if (this.hasLeaveByTarget) this.leaveByTarget.textContent = this.leaveByText(durationSeconds)

    this.routeSummaryTarget.classList.remove("hidden")
  }

  // "To arrive for the 08:00 visit, leave by 04:39" — start time minus the
  // route's own duration, both read off today's date (only the time of
  // day matters). "" when the visit named no time, or named one too far in
  // the past to be worth a leave-by (the controller never sends one then).
  leaveByText(durationSeconds) {
    if (!this.visitStartTimeValue) return ""

    const [ hours, minutes ] = this.visitStartTimeValue.split(":").map(Number)
    const start = new Date()
    start.setHours(hours, minutes, 0, 0)
    const leaveAt = new Date(start.getTime() - durationSeconds * 1000)
    const label = leaveAt.toTimeString().slice(0, 5)

    return `To arrive for the ${this.visitStartTimeValue} visit, leave by ${label}`
  }

  showError(reason) {
    if (this.hasRouteSummaryTarget) this.routeSummaryTarget.classList.add("hidden")
    if (this.hasApiErrorTarget) {
      if (this.hasApiErrorTextTarget) this.apiErrorTextTarget.textContent = `Map unavailable — ${reason}.`
      this.apiErrorTarget.classList.remove("hidden")
    }
  }

  hideError() {
    if (this.hasApiErrorTarget) this.apiErrorTarget.classList.add("hidden")
  }

  // Keeps the footer's Google/Apple deep links honest once an origin is
  // known — they work off the ORIGINAL href's destination param, so this
  // only ever adds/replaces the origin.
  updateMapLinks(lat, lng) {
    const origin = `${lat},${lng}`

    if (this.hasAppleLinkTarget) {
      const url = new URL(this.appleLinkTarget.href)
      url.searchParams.set("saddr", origin)
      this.appleLinkTarget.href = url.toString()
    }
    if (this.hasGoogleLinkTarget) {
      const url = new URL(this.googleLinkTarget.href)
      url.searchParams.set("origin", origin)
      this.googleLinkTarget.href = url.toString()
    }
  }

  copyAddress(event) {
    const button = event.currentTarget
    const text = button.dataset.address
    if (!text || !navigator.clipboard) return

    navigator.clipboard.writeText(text).then(() => {
      const original = button.textContent
      button.textContent = "Copied!"
      setTimeout(() => { button.textContent = original }, 2000)
    })
  }

  // Lazy dynamic import (matching compare_map_controller.js) — this modal
  // is opened occasionally, not on every page load, so mapbox-gl has no
  // business sitting in the main bundle for everyone else.
  async loadMapboxGL() {
    if (window.mapboxgl) return window.mapboxgl

    try {
      const mod = await import("mapbox-gl")
      window.mapboxgl = mod.default
      return window.mapboxgl
    } catch {
      console.error("site-directions: failed to load mapbox-gl")
      return null
    }
  }
}
